import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { IconButton } from '@socialchorus/shared-ui-components';
import { usePatronSelector } from '../../common/use-patron-selector';
import programSelectors from '../../models/program/selectors';
import {
  useChatbotThreadListQuery,
  useChatbotUpdateThreadMutation,
  useChatbotArchiveThreadMutation,
} from '../../common/queries/use-chatbot-query';
import ChatHistoryItem from '../../components/chatbot/ChatHistoryItem';
import ChatHistorySearch from '../../components/chatbot/ChatHistorySearch';
import styles from './chat-history-panel.module.scss';

export interface ChatHistoryPanelProps {
  onClose: () => void;
  onSelectThread: (threadId: string) => void;
}

type HistoryTab = 'chat' | 'search';

export const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  onClose,
  onSelectThread,
}) => {
  const { t } = useTranslation();
  const programId = usePatronSelector(programSelectors.getProgramId);
  const programIdStr = String(programId);
  const [searchActive, setSearchActive] = useState(false);
  const [activeTab, setActiveTab] = useState<HistoryTab>('chat');

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useChatbotThreadListQuery(programIdStr, 20, activeTab, {
      enabled: !!programId,
    });

  const updateMutation = useChatbotUpdateThreadMutation(programIdStr);
  const archiveMutation = useChatbotArchiveThreadMutation(programIdStr);

  const threads = data?.pages?.flatMap((page) => page.data) ?? [];

  // Infinite scroll: use the threadList div as the IntersectionObserver root so
  // the sentinel (inside overflow-y:auto) is correctly watched within its own
  // scroll container rather than the browser viewport.
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const sentinel = sentinelRef.current;
    if (!list || !sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: list, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleRename = useCallback(
    (threadId: string, newTitle: string) => {
      updateMutation.mutate({ threadId, title: newTitle });
    },
    [updateMutation]
  );

  const handleArchive = useCallback(
    (threadId: string) => {
      archiveMutation.mutate({ threadId });
    },
    [archiveMutation]
  );

  return (
    <div className={styles.chatHistoryPanel} data-testid="chat-history-panel">
      <div className={styles.panelHeader}>
        <IconButton
          iconName="arrow_back"
          onClick={onClose}
          title={t('common.back')}
          aria-label={t('common.back')}
        />
        <span className={cx('text-heading-bold', styles.panelTitle)}>
          {t('assistant.history', 'History')}
        </span>
      </div>

      <ChatHistorySearch
        onSelectThread={onSelectThread}
        onSearchActiveChange={setSearchActive}
      />

      {!searchActive && (
        <div className={styles.tabBar} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'chat'}
            className={cx(styles.tab, activeTab === 'chat' && styles.tabActive)}
            onClick={() => setActiveTab('chat')}
          >
            {t('assistant.historyTabChats', 'Chats')}
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'search'}
            className={cx(
              styles.tab,
              activeTab === 'search' && styles.tabActive
            )}
            onClick={() => setActiveTab('search')}
          >
            {t('assistant.historyTabSearches', 'Searches')}
          </button>
        </div>
      )}

      {!searchActive && (
        <div className={styles.threadList} ref={listRef}>
          {isLoading && (
            <div className={styles.loadingState} data-testid="history-loading">
              <span className={styles.loadingText}>
                {t('common.loading', 'Loading...')}
              </span>
            </div>
          )}

          {!isLoading && threads.length === 0 && (
            <div className={styles.emptyState} data-testid="history-empty">
              <div
                className={cx(
                  'text-body-1 text-black-90',
                  styles.emptyStateTitle
                )}
              >
                {activeTab === 'search'
                  ? t('assistant.noSearchHistory', 'No searches yet')
                  : t('assistant.noHistory', 'No conversations yet')}
              </div>
              <div className={styles.emptyStateDescription}>
                {activeTab === 'search'
                  ? t(
                      'assistant.noSearchHistoryDescription',
                      'Your AI search summaries will appear here.'
                    )
                  : t(
                      'assistant.noHistoryDescription',
                      'Your chat conversations will appear here.'
                    )}
              </div>
            </div>
          )}

          {!isLoading &&
            threads.map((thread) => (
              <ChatHistoryItem
                key={thread.id}
                thread={thread}
                isSelected={false}
                onSelect={onSelectThread}
                onRename={handleRename}
                onArchive={handleArchive}
              />
            ))}
          {isFetchingNextPage && (
            <div className={styles.loadingState}>
              <span className={styles.loadingText}>
                {t('common.loading', 'Loading...')}
              </span>
            </div>
          )}
          <div
            ref={sentinelRef}
            className={styles.loadMoreTrigger}
            data-testid="history-load-more-trigger"
          />
        </div>
      )}
    </div>
  );
};

export default ChatHistoryPanel;
